import React from "react";
import styled from "styled-components";
import { ResponsiveHeatMap } from "@nivo/heatmap";

import {
  Card,
  ElementContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  compassGreyBackground,
  darkGreen,
  greyText,
  lightGreyOutline,
} from "../../styles";
import {
  ManagerId,
  ManagerSinHeatmapData,
  SinV2,
} from "../../types/engagementAssessmentV2Types";
import { ChartEmptyState } from "./ChartEmptyState";
import { HeatmapLegend } from "./HeatmapLegend";
import { SIN_DISPLAY_ORDER, SIN_HEATMAP_COLOR_SCALE } from "./constants";

const ChartContainer = styled.div<{ rowCount: number }>`
  height: ${({ rowCount }): number => Math.max(rowCount * 44 + 120, 240)}px;
  width: 100%;
  flex-shrink: 0;
`;

const ScrollableChartArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const TooltipBox = styled.div`
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${lightGreyOutline};
  font-family: "Apercu";
  font-size: 12px;
  color: ${darkGreen};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
`;

const formatSinLabel = (sin: string): string => {
  const spaced = sin.toLowerCase().replace(/_/g, " ");
  if (spaced.length === 0) return spaced;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatIncidence = (value: number): string => value.toFixed(2);

type SinCellDatum = {
  x: SinV2;
  y: number | null;
  count: number;
};

type ManagerSeries = {
  id: ManagerId;
  data: SinCellDatum[];
};

const toNivoSeries = (data: ManagerSinHeatmapData): ManagerSeries[] => {
  const cellsByManager = new Map<ManagerId, Map<SinV2, SinCellDatum>>();
  for (const cell of data.cells) {
    let row = cellsByManager.get(cell.row);
    if (!row) {
      row = new Map<SinV2, SinCellDatum>();
      cellsByManager.set(cell.row, row);
    }
    row.set(cell.col, {
      x: cell.col,
      y: Number.isFinite(cell.value) ? cell.value : null,
      count: cell.count,
    });
  }

  return data.managers.map(({ id }) => {
    const row = cellsByManager.get(id);
    const cells: SinCellDatum[] = SIN_DISPLAY_ORDER.map((sin) => {
      const existing = row?.get(sin);
      return existing ?? { x: sin, y: null, count: 0 };
    });
    return { id, data: cells };
  });
};

export type ManagerSinHeatmapProps = {
  data: ManagerSinHeatmapData;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
};

export const ManagerSinHeatmap = ({
  data,
  isLoading,
  error,
  onRetry,
}: ManagerSinHeatmapProps): JSX.Element => {
  const managerNameById = React.useMemo(() => {
    const map = new Map<ManagerId, string>();
    for (const m of data.managers) map.set(m.id, m.name);
    return map;
  }, [data.managers]);

  const series = React.useMemo(() => toNivoSeries(data), [data]);
  const isEmpty = series.length === 0;

  return (
    <Card
      h="500px"
      isLoading={isLoading}
      style={{ display: "flex", flexDirection: "column", position: "relative" }}
    >
      <RowContainer mt={0} mb={4} jc="space-between" ai="center">
        <CompassParagraph fontSize={16} bold={true}>
          Manager × Sin heatmap
        </CompassParagraph>
      </RowContainer>
      <CompassParagraph fontSize={12} color={greyText} mb={12}>
        Sin incidence per manager (share of latest runs with evidenced sins)
      </CompassParagraph>

      {error && !isLoading ? (
        <ChartEmptyState
          variant="error"
          entityName="manager × sin heatmap"
          onRetry={onRetry}
        />
      ) : isEmpty && !isLoading ? (
        <ChartEmptyState variant="empty" entityName="manager × sin cells" />
      ) : (
        <>
          <ScrollableChartArea>
            <ChartContainer rowCount={series.length}>
              <ResponsiveHeatMap
                data={series}
                margin={{ top: 60, right: 20, bottom: 20, left: 180 }}
                axisTop={{
                  tickSize: 0,
                  tickPadding: 8,
                  tickRotation: -25,
                  format: (v: string): string => formatSinLabel(v),
                }}
                axisLeft={{
                  tickSize: 0,
                  tickPadding: 8,
                  tickRotation: 0,
                  format: (v: string): string =>
                    managerNameById.get(v as ManagerId) ?? v,
                }}
                colors={{
                  type: "quantize",
                  colors: [...SIN_HEATMAP_COLOR_SCALE],
                  domain: [0, 1],
                }}
                emptyColor={compassGreyBackground}
                borderRadius={3}
                borderWidth={1}
                borderColor={lightGreyOutline}
                enableLabels={true}
                label={(cell): string => {
                  if (cell.value === null || cell.value === undefined) {
                    return "";
                  }
                  return formatIncidence(Number(cell.value));
                }}
                labelTextColor={{
                  from: "color",
                  modifiers: [["darker", 2.5]],
                }}
                tooltip={({ cell }): JSX.Element => {
                  const managerName =
                    managerNameById.get(String(cell.serieId) as ManagerId) ??
                    String(cell.serieId);
                  const sinName = formatSinLabel(String(cell.data.x));
                  const value = cell.value;
                  const count = cell.data.count;
                  return (
                    <TooltipBox>
                      <strong>{managerName}</strong>
                      <br />
                      {sinName}
                      <br />
                      Incidence:{" "}
                      {value === null || value === undefined
                        ? "—"
                        : formatIncidence(Number(value))}
                      <br />
                      Engagements: {count}
                    </TooltipBox>
                  );
                }}
                theme={{
                  text: {
                    fontFamily: "Apercu",
                    fontSize: 11,
                    fill: darkGreen,
                  },
                  axis: {
                    ticks: {
                      text: {
                        fontFamily: "Apercu",
                        fontSize: 11,
                        fill: greyText,
                      },
                    },
                  },
                }}
                animate={true}
                motionConfig="gentle"
              />
            </ChartContainer>
          </ScrollableChartArea>
          <ElementContainer mt={8} w="100%">
            <HeatmapLegend variant="sin" />
          </ElementContainer>
        </>
      )}
    </Card>
  );
};
